<template>
  <div class="sv-table-view" data-test="sv-table-view">
    <!--
      The printed sheet's header. In the DOM always, hidden on screen: the print block below shows it
      only between this component's beforeprint and afterprint. It says what the screen says — the
      same appointments, the same ticks — nothing is re-fetched for paper.
    -->
    <header class="sv-table-print-head" data-test="sv-table-print-head">
      <div>
        <div class="sv-table-print-kicker" data-test="sv-table-print-kicker">{{ kicker }}</div>
        <h1 class="sv-table-print-title">{{ longDate }}</h1>
      </div>
      <div class="sv-table-print-stats" data-test="sv-table-print-stats">{{ stats }}</div>
      <div class="sv-table-print-legend" data-test="sv-table-print-legend">
        <span class="li"><span class="sv-table-lg-cb"></span>To-do</span>
        <span class="li"><span class="sv-table-lg-cb sv-table-lg-cb--on"><i class="fa fa-check" aria-hidden="true"></i></span>Done in Teamio</span>
        <span class="li"><span class="sv-table-lg-fyi">!</span>FYI</span>
        <span class="li"><span class="sv-table-lg-fyi sv-table-lg-fyi--crit">!</span>Clinical alert</span>
      </div>
    </header>

    <div class="sv-table-bar" data-test="sv-table-bar">
      <div class="sv-table-title">
        <span class="t" data-test="sv-table-title">Visit checklists</span>
        <span v-if="longDate" class="d" data-test="sv-table-date">{{ longDate }}</span>
      </div>

      <div class="sv-table-stats" data-test="sv-table-stats"><span
        v-for="(part, i) in statsParts"
        :key="i"
        :class="{ 'sv-table-num': part.num }"
      >{{ part.text }}</span></div>

      <!-- The order and Print travel as ONE group: when the bar is narrow (an iPad with the menu open)
           they wrap together to the right, instead of Print dropping alone to the left. -->
      <div class="sv-table-actions">
        <div class="sv-table-seg" role="group" aria-label="Order of the rows">
          <button
            v-for="opt in sortOptions"
            :key="opt.key"
            type="button"
            class="sv-table-seg__btn"
            :class="{ 'sv-table-seg__btn--on': sort === opt.key }"
            :aria-pressed="sort === opt.key ? 'true' : 'false'"
            :data-test="`sv-table-sort-${opt.key}`"
            @click="$emit('change-sort', opt.key)"
          >{{ opt.label }}</button>
        </div>

        <button type="button" class="sv-table-print-btn" data-test="sv-table-print" @click="print">
          <i class="fa fa-print" aria-hidden="true"></i>Print
        </button>
      </div>
    </div>

    <!--
      A rule that FAILED to run is not a clean day. Said above the table AND on the paper — a sheet
      that silently left a rule out would be believed.
    -->
    <p v-if="incomplete" class="sv-table-errors" data-test="sv-table-errors">{{ failedSentence }}</p>

    <div v-if="!appointments.length" class="sv-table-empty" data-test="sv-table-empty">
      <template v-if="filtersActive">
        No appointments match the current filter.
        <button type="button" class="sv-table-empty__clear" data-test="sv-table-clear-filters" @click="$emit('clear-filters')">Clear filters</button>
      </template>
      <template v-else>There are no appointments on this day.</template>
    </div>

    <div v-else class="sv-table-scroll" data-test="sv-table-scroll">
      <table class="sv-table" data-test="sv-table">
        <!-- ⛔ No <colgroup>: the widths are CSS on the th classes (the design canvas drops colgroups). -->
        <thead>
          <tr>
            <th class="c-time">Time</th>
            <th class="c-op">Op</th>
            <th class="c-pt">Patient · appt · insurance</th>
            <th class="c-alerts">Alerts</th>
            <th class="c-done">Done</th>
          </tr>
        </thead>
        <tbody v-for="group in groups" :key="group.key">
          <tr v-if="group.header" class="sv-table-group" :data-test="group.test">
            <td colspan="5">
              <span class="sv-table-group__g">
                <i :style="group.accent ? { background: group.accent } : null"></i>{{ group.name }}
                <span class="m">{{ group.meta }}</span>
              </span>
            </td>
          </tr>
          <ScheduleTableRow
            v-for="r in group.rows"
            :key="r.key"
            :appt="r.appt"
            :row="r.row"
            :op-name="r.opName"
            :accent="r.accent"
            :accent-index="r.accentIndex"
            :provider="r.provider"
            :status-text="r.statusText"
            :bucket="r.bucket"
            :broken="r.broken"
            :alerts-ready="alertsReady"
            :incomplete="incomplete"
            @select-appointment="$emit('select-appointment', $event)"
            @toggle="$emit('toggle', $event)"
          />
        </tbody>
      </table>
    </div>

    <footer class="sv-table-print-foot" data-test="sv-table-print-foot">{{ footer }}</footer>
  </div>
</template>

<script>
/**
 * The Table view (Phase 11): the same day as the grid, one ROW per appointment, every row carrying
 * the visit checklist inline, with a Print button that prints it as a landscape sheet.
 *
 * Plan: docs/plans/2026-10-13-schedule-view-table-view-print-plan.md
 *
 * ⛔ THE ROWS COME FROM THE PAGE'S `checklists` (checklistByAptNum) — the very map the blocks, the
 * chip and the card read — through tableView.js rowFor. This file never builds a checklist, and
 * imports from checklist.js only `checkKey`. A table that built its own could show a line its block
 * does not. Guarded by cypress/node-tests/schedule-view-wiring.test.js.
 *
 * PRESENTATIONAL: props in, events out, no store. The page owns the ticks (a tick here is the same
 * tick as on the card), the Patient Panel and the filters. Props are the page's FILTERED day, so the
 * table can never show more than the grid does.
 *
 * ⛔ The chair colours come from the UNFILTERED day's operatories, like the grid's: a chair keeps its
 * colour when the office filters to it.
 *
 * ── PRINT ───────────────────────────────────────────────────────────────────────────────────────
 * Print is `window.print()` on THIS page. The real work is in beforeprint / afterprint, so Ctrl+P
 * prints the same sheet as the button, and only while this component is on screen (so Ctrl+P on the
 * grid is untouched). beforeprint puts `sv-table-printing` on <body> and injects the @page rule;
 * afterprint removes both — and so does beforeDestroy, so a view switch mid-print can never leave the
 * rest of the page hidden. The print rules (unscoped block below) are gated on `@media print` AND
 * that body class, so no other page's printing changes. Precedent: AdvancedTxPlannedV2.vue.
 *
 * ⛔ The @page rule is written ONLY as the string onBeforePrint injects — never in a stylesheet. A
 * stylesheet's @page would apply to every print in the app for as long as this page's CSS is loaded.
 *
 * ⛔ No prop may be named `patNum`, `siteID`, `loading` or `allSettings`: mixin.js defines computeds
 * with those names and a computed SILENTLY REPLACES a same-named prop.
 */
import moment from 'moment-timezone';
import ScheduleTableRow from './ScheduleTableRow.vue';
import { checkKey } from '@/utils/scheduleView/checklist';
import { hasAlertErrors } from '@/utils/scheduleView/alerts';
import { ALERT_META } from '@/utils/scheduleView/settingsForm';
import { resolveBucket, statusLabel, isBroken } from '@/utils/scheduleView/statusBuckets';
import {
  appointmentSpan, appointmentProviderNum, operatoryPalette, OPERATORY_ACCENTS,
} from '@/utils/scheduleView/layout';
import {
  rowFor, orderAppointments, groupByChair, groupProviders, tableTotals, statsLine,
} from '@/utils/scheduleView/tableView';

/** The class this component puts on <body> between beforeprint and afterprint. */
const PRINT_BODY_CLASS = 'sv-table-printing';
/** The <style> element that carries the @page rule while printing. */
const PAGE_RULE_ID = 'sv-table-page';

const RULE_LABELS = {};
ALERT_META.forEach((a) => { RULE_LABELS[a.key] = a.label; });

export default {
  name: 'ScheduleTableView',

  components: { ScheduleTableRow },

  props: {
    /** The FILTERED day's appointments. */
    appointments: { type: Array, default: () => [] },
    /** The FILTERED day's operatories (an operatory filter removes chairs): the group rows and the order. */
    operatories: { type: Array, default: () => [] },
    /** The UNFILTERED day, for the chair colours and names. */
    unfilteredDay: { type: Object, default: null },
    providers: { type: Array, default: () => [] },
    /** checklistByAptNum — { '<AptNum>': { alertKeys, categories, totals } }. */
    checklists: { type: Object, default: () => ({}) },
    /** The store's ticks, '<AptNum>:<ItemKey>' → { by, at }. */
    checks: { type: Object, default: () => ({}) },
    bucketMap: { type: Object, default: () => ({}) },
    /** The /alerts payload for THIS date; null until it answers. */
    alertPayload: { type: Object, default: null },
    /** 'time' | 'op' */
    sort: { type: String, default: 'time' },
    /** YYYY-MM-DD, the day on screen, in the site's timezone. */
    date: { type: String, default: '' },
    timezone: { type: String, default: '' },
    /** 'MM/DD/YYYY' or 'DD/MM/YYYY' — the office's one date rule, for the printed time stamp. */
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    /** The practice's name for the printed kicker; empty prints "Visit checklists" alone. */
    practiceName: { type: String, default: '' },
    /** A filter is narrowing the day: an empty table then says so and offers to clear it. */
    filtersActive: { type: Boolean, default: false },
  },

  data() {
    return {
      /** Set at the moment of printing, in the site's timezone. */
      printedAt: '',
      sortOptions: [
        { key: 'time', label: 'By time' },
        { key: 'op', label: 'By operatory' },
      ],
    };
  },

  computed: {
    alertsReady() {
      return !!this.alertPayload;
    },

    incomplete() {
      return hasAlertErrors(this.alertPayload);
    },

    failedSentence() {
      const keys = (this.alertPayload && this.alertPayload.errors) || [];
      const names = keys.map(k => RULE_LABELS[k] || k);
      return `Could not check: ${names.join(', ')} — the rows below do not include ${names.length === 1 ? 'it' : 'them'}.`;
    },

    /** OperatoryNum -> { tint, accent }, from the UNFILTERED day. */
    palette() {
      return operatoryPalette(this.unfilteredDay ? this.unfilteredDay.operatories : this.operatories);
    },

    /** OperatoryNum -> the chair, by name, from the UNFILTERED day. */
    chairByNum() {
      const out = {};
      ((this.unfilteredDay && this.unfilteredDay.operatories) || this.operatories).forEach((op) => {
        if (op) out[op.OperatoryNum] = op;
      });
      return out;
    },

    sortDeps() {
      return {
        startOf: (appt) => {
          const span = appointmentSpan(appt);
          return span ? span.start : null;
        },
        opIndexOf: appt => this.operatories.findIndex(op => op && op.OperatoryNum === appt.Op),
      };
    },

    ordered() {
      return orderAppointments(this.appointments, this.sort, this.sortDeps);
    },

    /** [{ key, test, header, name, accent, meta, rows }] — one tbody each. */
    groups() {
      if (!this.ordered.length) return [];
      if (this.sort !== 'op') {
        return [{ key: 'all', test: '', header: false, name: '', accent: '', meta: '', rows: this.ordered.map(this.rowModel) }];
      }
      return groupByChair(this.ordered, this.operatories).map((g) => {
        const providers = groupProviders(g.appointments, this.providers, { providerNumOf: appointmentProviderNum });
        const patients = tableTotals(g.appointments, {}).patients || g.appointments.length;
        const count = `${patients} ${patients === 1 ? 'patient' : 'patients'}`;
        const colour = g.op ? this.palette[g.op.OperatoryNum] : null;
        return {
          key: g.op ? `op-${g.op.OperatoryNum}` : 'none',
          test: g.op ? `sv-table-group-${g.op.OperatoryNum}` : 'sv-table-group-none',
          header: true,
          name: g.op ? (g.op.OpName || g.op.Abbrev || `Op ${g.op.OperatoryNum}`) : 'No operatory',
          accent: colour ? colour.accent : '',
          meta: providers.concat([count]).join(' · '),
          rows: g.appointments.map(this.rowModel),
        };
      });
    },

    totals() {
      return tableTotals(this.appointments, this.checklists);
    },

    stats() {
      return statsLine(this.totals, { alertsReady: this.alertsReady });
    },

    /** The stats line cut into runs so the numbers can be bold; the runs join back into `stats`. */
    statsParts() {
      return this.stats.split(/(\d+)/).filter(s => s !== '').map(text => ({ text, num: /^\d+$/.test(text) }));
    },

    /** "Monday, September 21, 2026" — parsed as UTC so the viewer's timezone cannot move the day. */
    longDate() {
      const m = moment.utc(this.date, 'YYYY-MM-DD', true);
      return m.isValid() ? m.format('dddd, MMMM D, YYYY') : '';
    },

    kicker() {
      const name = String(this.practiceName || '').trim();
      return name ? `${name} · Visit checklists` : 'Visit checklists';
    },

    footer() {
      return this.printedAt
        ? `Printed from Teamio · ${this.printedAt} · checkmarks reflect Teamio at time of printing`
        : 'Printed from Teamio · checkmarks reflect Teamio at time of printing';
    },
  },

  mounted() {
    // Listeners rather than only wiring the button, so Ctrl+P produces the same sheet as clicking Print.
    window.addEventListener('beforeprint', this.onBeforePrint);
    window.addEventListener('afterprint', this.onAfterPrint);
  },

  beforeDestroy() {
    window.removeEventListener('beforeprint', this.onBeforePrint);
    window.removeEventListener('afterprint', this.onAfterPrint);
    // Switching view (or leaving the page) mid-print must never leave the rest of the page hidden.
    this.onAfterPrint();
  },

  methods: {
    /** Everything one row needs that is not in the row model: chair, provider, status. */
    rowModel(appt) {
      const chair = this.chairByNum[appt.Op] || null;
      const colour = chair ? this.palette[appt.Op] : null;
      const provNum = appointmentProviderNum(appt);
      const bucket = resolveBucket(appt, this.bucketMap);
      return {
        key: appt.AptNum,
        appt,
        row: rowFor(appt, this.checklists[appt.AptNum], this.checks, { checkKey }),
        opName: chair ? (chair.OpName || chair.Abbrev || '') : '',
        accent: colour ? colour.accent : '',
        accentIndex: colour ? OPERATORY_ACCENTS.indexOf(colour.accent) : -1,
        provider: this.providers.find(p => p && Number(p.ProvNum) === provNum) || null,
        statusText: statusLabel(appt, bucket),
        bucket,
        broken: isBroken(appt),
      };
    },

    print() {
      // beforeprint does the real work — it fires for Ctrl+P too.
      window.print();
    },

    /** Today's date and time in the SITE's timezone, as the office writes dates: "10/13/2026 9:41 am". */
    printStamp() {
      const zone = this.timezone && moment.tz.zone(this.timezone) ? this.timezone : null;
      const now = zone ? moment.tz(zone) : moment();
      const day = String(this.dateFormat || 'MM/DD/YYYY').replace('MM', 'M').replace('DD', 'D');
      return now.format(`${day} h:mm a`);
    },

    onBeforePrint() {
      this.printedAt = this.printStamp();
      document.body.classList.add(PRINT_BODY_CLASS);
      this.removePageRule();
      const rule = document.createElement('style');
      rule.id = PAGE_RULE_ID;
      rule.textContent = '@page { size: landscape; margin: 0.4in; }';
      document.head.appendChild(rule);
    },

    /** Must be idempotent — it also runs on unmount as a safety net. */
    onAfterPrint() {
      document.body.classList.remove(PRINT_BODY_CLASS);
      this.removePageRule();
    },

    removePageRule() {
      const rule = document.getElementById(PAGE_RULE_ID);
      if (rule && rule.parentNode) rule.parentNode.removeChild(rule);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

// ── the bar ───────────────────────────────────────────────────────────────────────────────────────
.sv-table-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  padding: 2px 0 12px;
}

.sv-table-title {
  display: flex;
  align-items: baseline;
  gap: 10px;

  .t {
    font-size: 16px;
    font-weight: 800;
    letter-spacing: -0.015em;
    color: $sv-ink;
  }

  .d {
    font-size: 12.5px;
    font-weight: 600;
    color: $sv-ink-3;
  }
}

.sv-table-stats {
  font-size: 12.5px;
  color: $sv-ink-3;
}

.sv-table-num {
  color: $sv-ink;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

// Pushed to the right edge; wraps as one group (see the template).
.sv-table-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.sv-table-seg {
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: $sv-radius;
  background: $sv-bg-3;
}

.sv-table-seg__btn {
  height: 28px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: $sv-ink-3;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: 1px;
  }
}

.sv-table-seg__btn--on {
  background: $sv-surface;
  color: $sv-ink;
  box-shadow: 0 1px 2px rgba(21, 32, 26, 0.12);
}

.sv-table-print-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  border: 1px solid $sv-brand;
  border-radius: $sv-radius;
  background: $sv-brand;
  color: #ffffff;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover { filter: brightness(1.06); }

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: 2px;
  }
}

.sv-table-errors {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: $sv-late-ink;
}

.sv-table-empty {
  padding: 24px;
  border: 1px dashed $sv-line;
  border-radius: $sv-radius-lg;
  background: $sv-surface;
  color: $sv-ink-3;
  font-size: 14px;
}

.sv-table-empty__clear {
  display: block;
  height: 32px;
  margin-top: 8px;
  padding: 0 12px;
  border: 1px solid currentColor;
  border-radius: $sv-radius;
  background: transparent;
  color: $sv-brand;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

// ── the table ─────────────────────────────────────────────────────────────────────────────────────
// The scroll host is the ONLY thing that scrolls sideways (the table is wider than an iPad), so the
// page itself never does.
.sv-table-scroll {
  overflow: auto;
  border: 1px solid $sv-line;
  border-radius: $sv-radius-lg;
  background: $sv-surface;
}

.sv-table {
  width: 100%;
  min-width: 900px;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  color: $sv-ink;
  font-size: 12.5px;

  th {
    position: sticky;
    top: 0;
    z-index: 2;
    padding: 10px 10px 9px;
    border-right: 1px solid $sv-brand-tint;
    border-bottom: 1px solid $sv-brand-tint;
    background: $sv-brand-soft;
    color: $sv-brand-deep;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.07em;
    text-align: left;
    text-transform: uppercase;
    vertical-align: bottom;

    &:last-child {
      border-right: 0;
      text-align: center;
    }
  }

  // Widths are CSS on the header cells (table-layout: fixed reads the first row); Alerts takes the rest.
  .c-time { width: 78px; }
  .c-op { width: 96px; }
  .c-pt { width: 26%; }
  .c-done { width: 66px; }

  // The host draws the bottom edge; the last row must not add a second line over it.
  ::v-deep tbody:last-child tr:last-child td { border-bottom: 0; }
}

.sv-table-group td {
  padding: 9px 12px;
  border-bottom: 1px solid $sv-line;
  background: $sv-bg-2;
}

.sv-table-group__g {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 12.5px;
  font-weight: 800;

  i {
    flex: 0 0 auto;
    width: 10px;
    height: 10px;
    border-radius: 3px;
    background: $sv-ink-4;
  }

  .m {
    font-weight: 500;
    color: $sv-ink-3;
  }
}

// ── the sheet's own header and footer (hidden on screen; the print block below shows them) ────────
.sv-table-print-head,
.sv-table-print-foot {
  display: none;
}

.sv-table-print-head {
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px 22px;
  padding-bottom: 10px;
  border-bottom: 2px solid $sv-ink;
}

.sv-table-print-kicker {
  color: $sv-brand;
  font-size: 8.5pt;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.sv-table-print-title {
  margin: 2px 0 0;
  color: $sv-ink;
  font-size: 18pt;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.sv-table-print-stats {
  padding-bottom: 2px;
  color: $sv-ink-2;
  font-size: 10pt;
}

.sv-table-print-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
  padding-bottom: 2px;
  color: $sv-ink-2;
  font-size: 9pt;

  .li {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.sv-table-lg-cb {
  display: grid;
  place-items: center;
  width: 11px;
  height: 11px;
  box-sizing: border-box;
  border: 1.5px solid $sv-ink-4;
  border-radius: 4px;
  background: $sv-surface;
  color: #ffffff;

  .fa { font-size: 7.5px; line-height: 1; }

  &--on {
    background: $sv-brand;
    border-color: $sv-brand;
  }
}

.sv-table-lg-fyi {
  display: grid;
  place-items: center;
  width: 11px;
  height: 11px;
  border-radius: 99px;
  background: $sv-amber-soft;
  color: $sv-fyi-ink;
  font-size: 7.5pt;
  font-weight: 800;
  line-height: 1;

  &--crit {
    background: $sv-rose;
    color: #ffffff;
  }
}

.sv-table-print-foot {
  margin-top: 10px;
  color: $sv-ink-3;
  font-size: 8pt;
}
</style>

<style lang="scss">
// ══ THE PRINTED SHEET ═════════════════════════════════════════════════════════════════════════════
// ⛔ Unscoped on purpose — it has to reach the page, the layout and the toast container around this
// component — and therefore gated TWICE: `@media print` AND `body.sv-table-printing`, a class that
// exists only between this component's beforeprint and afterprint. Nothing here can change how any
// other page prints, or how anything looks on screen. The selectors are deep enough to out-rank the
// scoped rules of the three Schedule View components they override. The @page rule is NOT here: it
// is injected as a <style id="sv-table-page"> by onBeforePrint and removed by onAfterPrint.
@import '../tokens';

@media print {
  body.sv-table-printing {
    // Tags, pills and markers keep their colour on paper (browsers drop backgrounds by default).
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;

    // Paper is white. With the browser's "Background graphics" on, the app's tinted body / content
    // background would print as a grey wash behind the whole sheet (AdvancedTxPlannedV2.scss does
    // the same for the same reason).
    background: #ffffff !important;

    // 1. Everything that is not the sheet.
    .sv-tabs,
    .sv-toolbar,
    .sv-controls,
    .sv-summary,
    .sv-hidden,
    .sv-table-bar,
    .sv-slideout-host,
    .copilotKitSidebar,
    [data-test="schedule-patient-panel-root"],
    [data-test="schedule-patient-panel"],
    [data-test="sv-checklist-layer"],
    .Vue-Toastification__container,
    .contentFooter {
      display: none !important;
    }

    // 2. The page gives the sheet the whole paper.
    .content,
    .schedule-view {
      padding: 0;
      background: #ffffff !important;
    }

    // A scrolling host prints ONE viewport's worth (the lesson AdvancedTxPlannedV2 learned): let it grow.
    .sv-table-view .sv-table-scroll {
      overflow: visible;
      height: auto;
      border: 0;
      border-radius: 0;
      background: none;
    }

    // 3. The header and the footer of the sheet.
    .sv-table-view .sv-table-print-head { display: flex; }
    .sv-table-view .sv-table-print-foot { display: block; }

    .sv-table-view .sv-table-errors {
      margin: 6px 0;
      font-size: 9pt;
    }

    // 4. The table, at paper sizes (the design export's `.hd-table.print`).
    .sv-table-view .sv-table {
      min-width: 0;
      font-size: 10pt;

      th {
        position: static;
        padding: 8px 6px 5px;
        border-right: 0;
        border-bottom: 1.5px solid $sv-ink;
        background: none;
        color: $sv-ink;
        font-size: 7.5pt;
      }

      // ⛔ The design's 50 / 64 clipped "8:00 am" and wrapped "60 min" on a real page (found in a
      // printed PDF). Wide enough for "12:00 pm" and "120 min" each on ONE line at the sizes below.
      th.c-time { width: 76px; }
      th.c-op { width: 72px; }
      th.c-pt { width: 24%; }
      th.c-done { width: 44px; }

      tr { break-inside: avoid; page-break-inside: avoid; }

      td {
        padding: 6px;
        border-right: 1px solid $sv-line;
        border-bottom: 1px solid $sv-line-print;
        background: none;
      }

      .sv-table-row--all td { background: none; }

      // A broken visit is already struck through and labelled "Broken": on paper it prints at FULL
      // strength. 55% ink on a mono printer is a faint grey at 9pt, and a faint line is read as
      // "nothing here" — legible wins.
      .sv-table-row--broken td { opacity: 1; }

      .sv-table-group td {
        padding: 8px 6px 4px;
        border-bottom: 1px solid $sv-ink;
        background: none;
      }

      .sv-table-group__g { font-size: 9.5pt; }

      // The time and the length never wrap or clip: one line each (the cell is also given a little
      // less side padding so "12:00 pm" fits its column with room to spare).
      .sv-table-time {
        padding-right: 4px;
        padding-left: 4px;
        white-space: nowrap;
      }

      .sv-table-time .t,
      .sv-table-time .p,
      .sv-table-time .dur { white-space: nowrap; }

      .sv-table-time .t { font-size: 10.5pt; }
      .sv-table-time .p,
      .sv-table-time .dur { font-size: 8pt; }

      .sv-table-op {
        height: auto;
        padding: 1px 5px;
        font-size: 8.5pt;
      }

      .sv-table-prov {
        margin-top: 4px;
        font-size: 8pt;

        .av { display: none; }
      }

      // The name is plain text on paper: no link colour, no underline, nothing to click.
      .sv-table-name {
        color: inherit;
        font-size: 10.5pt;
        text-decoration: none;
        cursor: default;
      }

      // ⛔ A line the keyboard last touched still has focus when the browser prints, and its
      // focus ring would print as a teal box around that one item (found in a printed PDF after a
      // Tab + Space tick). Nothing on paper is "focused", "hovered" or a control.
      .sv-table-item,
      .sv-table-name {
        outline: none;
        box-shadow: none;
        background: none;

        &:hover,
        &:focus,
        &:focus-visible {
          outline: none;
          box-shadow: none;
          background: none;
        }
      }

      .sv-table-row--broken .sv-table-name { text-decoration: line-through; }

      .sv-table-age,
      .sv-table-ins { font-size: 8.5pt; }

      .sv-table-st { font-size: 7pt; }

      .sv-table-procs {
        margin-top: 2px;
        font-size: 9pt;
      }

      .sv-table-list {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 2px 14px;
      }

      .sv-table-item {
        width: 100%;
        margin: 0;
        padding: 1px 0;
        gap: 5px;
        font-size: 9pt;
        line-height: 1.3;
      }

      .sv-table-cb,
      .sv-table-fyi {
        width: 11px;
        height: 11px;
        margin-top: 1px;
        font-size: 7.5pt;
      }

      .sv-table-cb .fa { font-size: 7px; }

      // A ticked box is drawn in INK — white box, black border, black check — so a mono printer
      // keeps the tick visible (a teal fill would print as a grey square).
      .sv-table-item--done .sv-table-cb {
        background: $sv-surface;
        border-color: $sv-ink;
        color: $sv-ink;
      }

      .sv-table-tag {
        font-size: 6.5pt;
        line-height: 11px;
      }

      .sv-table-meta { font-size: 8.5pt; }

      // 5. The Done cell is a box and n/n on paper, not a ring.
      .sv-table-ring { display: none; }

      .sv-table-pbox {
        display: grid;
        place-items: center;
        width: 15px;
        height: 15px;
        box-sizing: border-box;
        margin: 0 auto 3px;
        border: 1.5px solid $sv-ink;
        border-radius: 3px;
        background: none;
        color: $sv-ink;

        .fa { font-size: 9px; line-height: 1; }
      }

      .sv-table-pdone {
        display: block;
        color: $sv-ink-3;
        font-size: 7.5pt;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
    }
  }
}
</style>
